export type OrdinaryHoursCapAnchorKind =
  | 'ISO_WEEK'
  | 'ROLLING_CONSECUTIVE'
  | 'FIXED_ACCOUNTING_PERIOD';

export interface OrdinaryHoursCapWindowDefinition {
  capSetId: string;
  capSetDigestSha256: string;
  windowId: string;
  code: string;
  windowDays: number;
  maximumHours: number;
  anchorKind: OrdinaryHoursCapAnchorKind;
  anchorDate: string | null;
  clauseReference: string;
  sourceReference: string;
}

export interface ResolvedOrdinaryHoursCapWindow extends OrdinaryHoursCapWindowDefinition {
  windowStartDate: string;
  windowEndDate: string;
  ordinaryHoursWorkedBeforeShift: number;
}

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;

export class InvalidOrdinaryHoursCapWindowError extends Error {
  readonly code = 'INVALID_ORDINARY_HOURS_CAP_WINDOW';

  constructor(message: string) {
    super(message);
    this.name = 'InvalidOrdinaryHoursCapWindowError';
  }
}

function parseYmdUtc(ymd: string, field: string): number {
  if (!YMD_PATTERN.test(ymd)) {
    throw new InvalidOrdinaryHoursCapWindowError(`${field} must be YYYY-MM-DD`);
  }
  const [year, month, day] = ymd.split('-').map(Number);
  const timestamp = Date.UTC(year!, month! - 1, day!);
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month! - 1 ||
    parsed.getUTCDate() !== day
  ) {
    throw new InvalidOrdinaryHoursCapWindowError(`${field} is not a valid calendar date`);
  }
  return timestamp;
}

function ymdFromUtc(timestamp: number): string {
  // eslint-disable-next-line statura/no-utc-date-string -- cap-window bounds are UTC-scoped YMD anchors, not tenant-local business dates
  return new Date(timestamp).toISOString().slice(0, 10);
}

function requiredText(value: string, field: string, maxLength = 500): string {
  const normalized = value.trim();
  if (!normalized) {
    throw new InvalidOrdinaryHoursCapWindowError(`${field} is required`);
  }
  if (normalized.length > maxLength) {
    throw new InvalidOrdinaryHoursCapWindowError(`${field} exceeds ${maxLength} characters`);
  }
  return normalized;
}

export function validateOrdinaryHoursCapWindowDefinitions(
  windows: readonly OrdinaryHoursCapWindowDefinition[],
): void {
  if (windows.length === 0) {
    throw new InvalidOrdinaryHoursCapWindowError(
      'An explicit ordinary-hours cap set must contain at least one window',
    );
  }
  if (windows.length > 8) {
    throw new InvalidOrdinaryHoursCapWindowError(
      'An ordinary-hours cap set cannot contain more than eight windows',
    );
  }

  const ids = new Set<string>();
  const codes = new Set<string>();
  let capSetId: string | null = null;
  let capSetDigest: string | null = null;

  for (const window of windows) {
    const currentCapSetId = requiredText(window.capSetId, 'capSetId', 100);
    const currentDigest = requiredText(
      window.capSetDigestSha256,
      'capSetDigestSha256',
      64,
    ).toLowerCase();
    if (!SHA256_PATTERN.test(currentDigest)) {
      throw new InvalidOrdinaryHoursCapWindowError(
        'capSetDigestSha256 must be a lowercase SHA-256 hex digest',
      );
    }
    if (capSetId !== null && capSetId !== currentCapSetId) {
      throw new InvalidOrdinaryHoursCapWindowError(
        'All concurrent windows must belong to the same cap set',
      );
    }
    if (capSetDigest !== null && capSetDigest !== currentDigest) {
      throw new InvalidOrdinaryHoursCapWindowError(
        'All concurrent windows must carry the same cap-set digest',
      );
    }
    capSetId = currentCapSetId;
    capSetDigest = currentDigest;

    const windowId = requiredText(window.windowId, 'windowId', 100);
    const code = requiredText(window.code, 'code', 80).toUpperCase();
    if (ids.has(windowId)) {
      throw new InvalidOrdinaryHoursCapWindowError(`Duplicate windowId ${windowId}`);
    }
    if (codes.has(code)) {
      throw new InvalidOrdinaryHoursCapWindowError(`Duplicate cap code ${code}`);
    }
    if (!Number.isInteger(window.windowDays) || window.windowDays < 1 || window.windowDays > 366) {
      throw new InvalidOrdinaryHoursCapWindowError(
        `Window ${code} must use an integer duration between 1 and 366 days`,
      );
    }
    if (
      !Number.isFinite(window.maximumHours) ||
      window.maximumHours <= 0 ||
      window.maximumHours > 1000
    ) {
      throw new InvalidOrdinaryHoursCapWindowError(
        `Window ${code} maximumHours must be greater than zero and no more than 1000`,
      );
    }
    if (window.anchorKind === 'ISO_WEEK') {
      if (window.windowDays !== 7 || window.anchorDate !== null) {
        throw new InvalidOrdinaryHoursCapWindowError(
          `Window ${code} may use ISO_WEEK only with seven days and no anchor date`,
        );
      }
    } else if (window.anchorKind === 'ROLLING_CONSECUTIVE') {
      if (window.anchorDate !== null) {
        throw new InvalidOrdinaryHoursCapWindowError(
          `Window ${code} may not use an anchor date for a rolling-consecutive window`,
        );
      }
    } else if (window.anchorKind === 'FIXED_ACCOUNTING_PERIOD') {
      if (!window.anchorDate) {
        throw new InvalidOrdinaryHoursCapWindowError(
          `Window ${code} requires an accounting-period anchor date`,
        );
      }
      parseYmdUtc(window.anchorDate, `Window ${code} anchorDate`);
    } else {
      throw new InvalidOrdinaryHoursCapWindowError(
        `Window ${code} has unsupported anchor kind ${String(window.anchorKind)}`,
      );
    }
    requiredText(window.clauseReference, `Window ${code} clauseReference`, 500);
    requiredText(window.sourceReference, `Window ${code} sourceReference`, 1000);

    ids.add(windowId);
    codes.add(code);
  }
}

export function ordinaryHoursCapWindowBounds(
  definition: OrdinaryHoursCapWindowDefinition,
  eventDate: string,
): { windowStartDate: string; windowEndDate: string } {
  validateOrdinaryHoursCapWindowDefinitions([definition]);
  const eventTimestamp = parseYmdUtc(eventDate, 'eventDate');
  let startTimestamp: number;

  if (definition.anchorKind === 'ISO_WEEK') {
    const day = new Date(eventTimestamp).getUTCDay();
    const daysSinceMonday = (day + 6) % 7;
    startTimestamp = eventTimestamp - daysSinceMonday * 86_400_000;
  } else if (definition.anchorKind === 'ROLLING_CONSECUTIVE') {
    startTimestamp = eventTimestamp - (definition.windowDays - 1) * 86_400_000;
  } else {
    const anchorTimestamp = parseYmdUtc(definition.anchorDate!, 'anchorDate');
    const dayDelta = Math.floor((eventTimestamp - anchorTimestamp) / 86_400_000);
    const windowIndex = Math.floor(dayDelta / definition.windowDays);
    startTimestamp = anchorTimestamp + windowIndex * definition.windowDays * 86_400_000;
  }

  return {
    windowStartDate: ymdFromUtc(startTimestamp),
    windowEndDate:
      definition.anchorKind === 'ROLLING_CONSECUTIVE'
        ? eventDate
        : ymdFromUtc(startTimestamp + (definition.windowDays - 1) * 86_400_000),
  };
}

export function resolveOrdinaryHoursCapWindows(
  definitions: readonly OrdinaryHoursCapWindowDefinition[],
  eventDate: string,
  priorHoursByWindowId: ReadonlyMap<string, number>,
): ResolvedOrdinaryHoursCapWindow[] {
  validateOrdinaryHoursCapWindowDefinitions(definitions);
  return definitions.map((definition) => {
    const priorHours = priorHoursByWindowId.get(definition.windowId);
    if (priorHours === undefined || !Number.isFinite(priorHours) || priorHours < 0) {
      throw new InvalidOrdinaryHoursCapWindowError(
        `Window ${definition.code} requires authoritative non-negative prior ordinary hours`,
      );
    }
    return {
      ...definition,
      ...ordinaryHoursCapWindowBounds(definition, eventDate),
      ordinaryHoursWorkedBeforeShift: priorHours,
    };
  });
}

export function validateResolvedOrdinaryHoursCapWindows(
  windows: readonly ResolvedOrdinaryHoursCapWindow[],
): void {
  validateOrdinaryHoursCapWindowDefinitions(windows);
  for (const window of windows) {
    const start = parseYmdUtc(window.windowStartDate, `Window ${window.code} windowStartDate`);
    const end = parseYmdUtc(window.windowEndDate, `Window ${window.code} windowEndDate`);
    const inclusiveDays = Math.floor((end - start) / 86_400_000) + 1;
    if (inclusiveDays !== window.windowDays) {
      throw new InvalidOrdinaryHoursCapWindowError(
        `Window ${window.code} resolved bounds do not span ${window.windowDays} days`,
      );
    }
    if (
      !Number.isFinite(window.ordinaryHoursWorkedBeforeShift) ||
      window.ordinaryHoursWorkedBeforeShift < 0
    ) {
      throw new InvalidOrdinaryHoursCapWindowError(
        `Window ${window.code} requires authoritative non-negative prior ordinary hours`,
      );
    }
  }
}

export function ordinaryHoursCapAccumulatorKey(
  staffId: string,
  window: Pick<ResolvedOrdinaryHoursCapWindow, 'capSetId' | 'windowId' | 'windowStartDate'>,
): string {
  return `${staffId}|${window.capSetId}|${window.windowId}|${window.windowStartDate}`;
}

const HOURS_EPSILON = 1 / 10_000;

function roundHours(hours: number): number {
  return Math.round(hours * 10_000) / 10_000;
}

function finiteNonNegativeHours(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

export interface OrdinaryHoursCapCascadeResult {
  ordinaryHours: number;
  overtimeHours: number;
  capConsumedHours: number;
}

/**
 * Minimum ordinary headroom across concurrent cap windows before this shift
 * accrues any of its own hours.
 */
export function ordinaryHeadroomBeforeShift(
  windows: readonly ResolvedOrdinaryHoursCapWindow[],
  priorOrdinaryHoursOverride?: number,
): number {
  if (windows.length === 0) return Number.POSITIVE_INFINITY;
  validateResolvedOrdinaryHoursCapWindows(windows);
  const headrooms = windows.map((window) =>
    Math.max(
      0,
      window.maximumHours - (priorOrdinaryHoursOverride ?? window.ordinaryHoursWorkedBeforeShift),
    ),
  );
  return Math.min(...headrooms);
}

/**
 * Allocate ordinary vs overtime hours for one shift against concurrent cap
 * windows. Each ordinary minute is counted once for the whole shift (M6).
 */
export function allocateOrdinaryHoursUnderCapCascade(input: {
  shiftHours: number;
  capWindows?: readonly ResolvedOrdinaryHoursCapWindow[];
  priorOrdinaryHours?: number;
  dailyOrdinaryCap?: number;
}): OrdinaryHoursCapCascadeResult {
  const shiftHours = finiteNonNegativeHours(input.shiftHours);
  const dailyCap = finiteNonNegativeHours(input.dailyOrdinaryCap) || Number.POSITIVE_INFINITY;
  const dailyOrdinary = Math.min(shiftHours, dailyCap);

  const windows = input.capWindows ?? [];
  const headroom =
    windows.length > 0
      ? ordinaryHeadroomBeforeShift(windows, input.priorOrdinaryHours)
      : Number.POSITIVE_INFINITY;
  const capOrdinary = Math.min(dailyOrdinary, headroom);
  const ordinaryHours = roundHours(Math.max(0, capOrdinary));
  const overtimeHours = roundHours(Math.max(0, shiftHours - ordinaryHours));

  return {
    ordinaryHours,
    overtimeHours,
    capConsumedHours: ordinaryHours,
  };
}

/**
 * Legacy midnight-segment cap cascade that re-applied fortnight headroom per
 * calendar segment and double-counted ordinary hours (8 Sep M6). Retained for
 * regression pins only — never call from production paths.
 */
export function allocateOrdinaryHoursUnderCapCascadeLegacyMidnightDoubleCount(input: {
  segmentHours: readonly [number, number];
  capWindows: readonly ResolvedOrdinaryHoursCapWindow[];
  dailyOrdinaryCap?: number;
}): OrdinaryHoursCapCascadeResult {
  const [beforeMidnight, afterMidnight] = input.segmentHours;
  const first = allocateOrdinaryHoursUnderCapCascade({
    shiftHours: beforeMidnight,
    capWindows: input.capWindows,
    dailyOrdinaryCap: input.dailyOrdinaryCap,
  });
  const second = allocateOrdinaryHoursUnderCapCascade({
    shiftHours: afterMidnight,
    capWindows: input.capWindows,
    dailyOrdinaryCap: input.dailyOrdinaryCap,
  });
  const ordinaryHours = roundHours(first.ordinaryHours + second.ordinaryHours);
  const shiftHours = roundHours(beforeMidnight + afterMidnight);
  return {
    ordinaryHours,
    overtimeHours: roundHours(Math.max(0, shiftHours - ordinaryHours)),
    capConsumedHours: ordinaryHours,
  };
}

export function capConsumedMinutesNeverExceedShiftMinutes(
  shiftMinutes: number,
  capConsumedHours: number,
): boolean {
  if (!Number.isFinite(shiftMinutes) || shiftMinutes < 0) return false;
  if (!Number.isFinite(capConsumedHours) || capConsumedHours < 0) return false;
  return capConsumedHours * 60 <= shiftMinutes + HOURS_EPSILON * 60;
}
